<template>
  <div class="station-order-schedule-container">
    <div :class="['station-order-schedule-list', {'station-schedule-list-with-footer': displayFooterOptions}]">

      <div class="station-schedule-searchbar-container row no-gutters" v-if="displaySearchBar">
        <label :class="['align-middle', 'm-0', displaySearchBarAddon ? 'col-10' : 'col-12']">
          <i class="far fa-search station-schedule-search-icon align-middle"></i>
          <input type="text" class="form-control station-schedule-search-bar" v-model="searchText" placeholder="Search" />
          <button class="btn bg-transparent schedule-search-cancel-button" v-if="searchText" @click.prevent="clearSearch">
            <i class="fa fa-times"></i>
          </button>
        </label>
        <div v-if="displaySearchBarAddon" class="col-2">
          <slot
            name="searchAddon"
            :data="{ groupList: filteredScheduleGroupList, orderList: flattenedOrderList, orderCount: flattenedOrderList.length, isSearchActive: !!searchText }" 
          />
        </div>
      </div>

      <div class="text-center text-danger" v-if="orderScheduleError">{{orderScheduleError}}</div>
      <h3 class="text-center" v-if="searchText">Search Results for '{{searchText}}'</h3>
      <h3 class="text-center" v-if="!searchText && (filterOptimiserAvailableOnly || filterAvailableOnly || filterIgnoreHold)">Showing Order Available/Not on Hold Only</h3>
      <Spinner v-if="scheduleLoading" />
      <slot name="orderListHeader" :data="{ groupList: filteredScheduleGroupList, orderList: flattenedOrderList, orderCount: flattenedOrderList.length, isSearchActive: !!searchText }" />

      <div v-for="group of filteredScheduleGroupList" :key="`group-${group.key}`">
        <div :class="['station-schedule-header']" :style="{'color': group.colour}">
          {{group.header}}
          <div class="station-schedule-header-badge">{{group.groupCount}}</div>
        </div>

        <transition-group name="order-schedule-list" tag="div" class="station-batch-draggable-container">
          <StationOrderScheduleRow
            :id="order.id"
            :orderSeqNum="order.sequenceNumber"
            :orderCustomerRef="order.customerRef"
            :orderCustomer="order.contact.name"
            :colourHighlight="group.colour"
            :dueDate="order.dueDate"
            :isReady="order.isReady"
            :onHold="order.isOnHold"
            :stagePanelCount="order.stagePanelCount"
            :orderPanelCount="order.orderPanelCount"
            @click="orderClicked(order)"
            :ref="`order-${order.id}`"
            v-for="order of group.orderList" :key="`order-${order.id}`">

            <template v-slot:labels>              
              <LabelBadge labelClass="label-order-urgent" v-if="order.isUrgent">Urgent</LabelBadge>
              <LabelBadge labelClass="label-order-type-nonChargeableRework" v-if="order.isRecut">RECUT</LabelBadge>
              <LabelBadge labelClass="label-order-type-nonChargeableRework" v-if="order.isNCR">NCR</LabelBadge>
              <LabelBadge labelClass="label-order-hold" v-if="order.isOnHold">ON HOLD</LabelBadge>
              <LabelBadge labelClass="label-order-status-preProduction" v-if="order.status == OrderStatusTypes.preProduction">PRE-PRODUCTION</LabelBadge>
            </template>

          </StationOrderScheduleRow>
        </transition-group>
      </div>

      <div v-if="isEmptySchedule" class="text-center">
        <h2>{{emptyScheduleText}}</h2>
      </div>
      <div v-if="filteredScheduleGroupList.length == 0" class="text-center">
        <h2>No orders in the search result</h2>
      </div>

      <slot name="orderListFooter" :data="{ groupList: filteredScheduleGroupList, orderCount: flattenedOrderList.length, isSearchActive: !!searchText }" />

    </div>
    <div class="station-schedule-filterbar" v-if="displayFooterOptions">
      <div class="row no-gutters">
        <div class="col-5 px-1">
          <StationFilterButton v-if="!displayOptimiserMode" v-model="filterAvailableOnly" :badge="orderAvailableCount">Available Only</StationFilterButton>
          <StationFilterButton v-if="displayOptimiserMode" v-model="filterOptimiserAvailableOnly">Available Only</StationFilterButton>
        </div>
        <div class="col-3 px-1">
          <StationFilterButton v-model="filterIgnoreHold">Hide Hold</StationFilterButton>
        </div>
        <div class="col-4 px-2">
          <div class="row no-gutters station-schedule-page-area">
            <div class="col-3">
              <button class="btn btn-block btn-default" @click.prevent="setPageIndex(scheduleCurrentPage-1)" :disabled="scheduleCurrentPage == 0"><i class="far fa-angle-left"></i></button>
            </div>
            <div class="col-6 station-schedule-page-area-display">
              {{scheduleCurrentPage+1}} / {{schedulePageCount}}
            </div>
            <div class="col-3">
              <button class="btn btn-block btn-default" @click.prevent="setPageIndex(scheduleCurrentPage+1)" :disabled="scheduleCurrentPage == schedulePageCount-1"><i class="far fa-angle-right"></i></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.station-order-schedule-container {
  width: 100%;
  height: 100%;
  position: relative;
  font-size: 13px;
}
.station-order-schedule-list {
  position: absolute;
  width: 100%;
  top: 0;
  bottom: 0;
  overflow-y:scroll;  
}

/* Order Transitions */
.order-schedule-list-move {
  transition: transform 0.5s;
}
.order-schedule-list-leave {
  transition: all 0.5s;
}
.order-schedule-list-enter, .order-schedule-list-leave-to {
  opacity: 0;
  transform: translateX(-300px);
}

</style>

<script>
import _ from 'lodash'
import moment from 'moment'

import Utils from '@/utils/utils'
import StationOrderScheduleRow from '@/apps/glass-production-station/src/components/schedule/stationOrderScheduleRow'
import StationFilterButton from '@/apps/glass-production-station/src/components/station/stationFilterButton'

import { OrderStatusTypes } from '@/store/types'

export default {
  name: 'StationOrderScheduleList',
  components: {
    StationOrderScheduleRow,
    StationFilterButton
  },
  props: {
    displaySearchBar: {
      type: Boolean,
      default: true
    },
    displaySearchBarAddon: {
      type: Boolean,
      default: false
    },
    displayFooterOptions: {
      type: Boolean,
      default: true
    },
    displayOptimiserMode: {
      type: Boolean,
      default: false
    },
    emptyScheduleText: {
      type: String,
      default: 'No orders in the schedule for this station'
    },
    scheduleList: Array,
  },
  data() {
    return {
      searchText: '',
      filterAvailableOnly: false,
      filterOptimiserAvailableOnly: false,
      filterIgnoreHold: true,   // 26/11/24 - Now on by default
      scheduleLoading: true,
      orderScheduleError: '',
      orderScheduleData: {},
      orderAvailableCount: 0,

      schedulePageCount: 0,
      scheduleOffset: 0,
      schedulePageLength: 50,
      scheduleCurrentPage: 0,
      scheduleCurDraw: 0,
      scheduleRecvDraw: 0,

      scheduleListRequiresUpdate: true
    }
  },
  watch: {
    filterAvailableOnly: function() {
      this.scheduleCurrentPage = 0
      this.scheduleLoading = true
      this.debouncedSearchOrderScheduleList()
    },
    filterOptimiserAvailableOnly: function() {
      this.scheduleCurrentPage = 0
      this.scheduleLoading = true
      this.debouncedSearchOrderScheduleList()
    },
    filterIgnoreHold: function() {
      this.scheduleCurrentPage = 0
      this.scheduleLoading = true
      this.debouncedSearchOrderScheduleList()
    },
    searchText: function() {
      this.scheduleCurrentPage = 0
      this.scheduleLoading = true
      this.debouncedSearchOrderScheduleList()
    }
  },
  computed: {
    isEmptySchedule() {
      return (!this.orderScheduleData.groupList || this.orderScheduleData.groupList.length == 0)      
    },
    filteredScheduleGroupList() {
      var currentDate = moment().local()
      let urgentColour = '#EB4D4B'
      let todayColour = '#FBC530'
      let goodColour = '#4CD137'

      if(!this.orderScheduleData.groupList)
        return []

      var outputList = this.orderScheduleData.groupList.map(scheduleGroup => {
        
        var groupColour = '#4CD137'
        var groupHeader = ''

        if(scheduleGroup.options.isDespatchGroup) {
          groupHeader = 'DESPATCH'
        }
        else if(scheduleGroup.options.isCancelledGroup) {
          groupColour = todayColour
          groupHeader = 'CANCELLED'
        }
        else if(scheduleGroup.options.isCompletedGroup) {
          groupColour = goodColour
          groupHeader = 'COMPLETED'
        }
        else if(scheduleGroup.options.isIssueGroup) {
          groupColour = urgentColour
          groupHeader = 'ISSUES'
        }
        else if(scheduleGroup.options.isNCRGroup) {
          groupColour = urgentColour
          groupHeader = 'NCR'
        }
        else if(scheduleGroup.options.isOverdueGroup) {
          groupColour = urgentColour
          groupHeader = 'OVERDUE'
        }
        else {
          let groupDate = moment(scheduleGroup.options.date).local()
          
          // Today?
          if(groupDate.isSame(currentDate, 'day')) {
            groupColour = todayColour
            groupHeader = `TODAY - ${groupDate.format('Do MMM')}`
          } else {
            var dayName = groupDate.format('dddd')
            if(groupDate.isSame(currentDate.clone().add(1, 'days'), 'day'))
              dayName = 'Tomorrow'

            groupColour = goodColour
            groupHeader = `${dayName} - ${groupDate.format('Do MMM')}`
          }
        }

        return {
          header: groupHeader.toUpperCase(),
          colour: groupColour,
          key: scheduleGroup.key,
          groupCount: scheduleGroup.groupCount,
          orderList: scheduleGroup.orderList
        }
      })

      return outputList
    },
    flattenedOrderList() {
      let orderList = []
      this.filteredScheduleGroupList.forEach(group => {
        orderList = _.concat(orderList, group.orderList)
      })
      return orderList
    }
  },
  created() {
    this.debouncedSearchOrderScheduleList = _.debounce(this.requestOrderScheduleList, 250)
    this.debouncedNavigateOrderScheduleList = _.debounce(this.requestOrderScheduleList, 100)
    this.OrderStatusTypes = OrderStatusTypes
  },
  mounted() {
    this.scheduleLoading = true
    this.requestOrderScheduleList()

    // Setup a timer to refresh the schedule list every x seconds
    // We add a random offset so all stations do not send a request all at once which is impacting server performance
    this.$options.scheduleUpdateTimer = setInterval(this.updateScheduleRefreshTimer, (20 + _.random(-2.5, 2.5)) * 1000)

    // Subscribe to the generic panel schedule list (virtual endpoint, not actual one)
    // Any updates to production / orders will refresh this endpoint
    this.subscribeEndpoint('/production/panelschedule', () => {
      this.scheduleListRequiresUpdate = true
    })
  },
  beforeDestroy() {
    clearInterval(this.$options.scheduleUpdateTimer)
    this.unsubscribeEndpoint('/production/panelschedule')
  },
  methods: {
    clearSearch() {
      this.searchText = ''
    },
    orderClicked(order) {
      let orderComponent = this.$refs[`order-${order.id}`][0]

      this.$emit('orderClick', { order: order, target: orderComponent.$el })
    },
    setPageIndex(index) {
      if(index < 0 || index >= this.schedulePageCount)
        return

      this.scheduleLoading = true
      this.scheduleCurrentPage = index
      this.debouncedNavigateOrderScheduleList()
    },
    updateScheduleRefreshTimer() {
      if(this.scheduleListRequiresUpdate) {
        this.requestOrderScheduleList()
      }
    },

    // Reload the order schedule list
    // Called externally manually as required
    reloadOrderScheduleList() {
      this.requestOrderScheduleList()
    },

    // Load the order schedule list
    requestOrderScheduleList() {

      // Note: We don't set loading here explicitly as this function will be called very often due to panel update notifications via socket service
      // Only set loading when explicitly requested (search, page change, etc)
      this.scheduleOffset = this.scheduleCurrentPage * this.schedulePageLength
      this.scheduleListRequiresUpdate = false

      var params = {
        start: this.scheduleOffset,
        length: this.schedulePageLength,
        draw: this.scheduleCurDraw++,
        isAvailableOnly: this.filterAvailableOnly ? 1 : 0,
        ignoreOnHold: this.filterIgnoreHold ? 1 : 0,
        searchString: this.searchText != '' ? encodeURIComponent(this.searchText) : null
      }

      this.orderScheduleError = ''
      this.$apiService.glassProductionStationService.getOrderScheduleForStation(this.$route.params.id, params)
      .then(response => {
        // Discard results if the data is older than newer data we have
        if(parseInt(response.data.draw) >= this.scheduleRecvDraw) {
          this.orderScheduleData = response.data

          this.schedulePageCount = Math.ceil(this.orderScheduleData.totalOrders / this.schedulePageLength)

          this.scheduleCurrentPage = this.scheduleOffset / this.schedulePageLength
          this.scheduleCurrentPage = Math.min(this.scheduleCurrentPage, this.schedulePageCount-1)
          this.scheduleCurrentPage = Math.max(this.scheduleCurrentPage, 0)

          this.orderAvailableCount = this.orderScheduleData.totalOrdersAvailable

          this.scheduleRecvDraw = parseInt(response.data.draw)
          this.scheduleCurDraw = Math.max(this.scheduleCurDraw, this.scheduleRecvDraw)

          this.$emit('urgentCountUpdated', this.orderScheduleData.totalOrdersFlagged)
        }
      })
      .catch(err => {
        this.orderScheduleError = 'Error loading order schedule'
      })
      .finally(() => {
        this.scheduleLoading = false
      })
    },
  }
}
</script>